import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Invoice, Task } from '@/types';
import { invoicesApi } from '@/api/endpoints/invoices';
import toast from 'react-hot-toast';
import { TASK_STATUS } from '@/utils/constants';

interface InvoiceState {
  invoices: Invoice[];
  pendingTasks: Task[];
  selectedInvoice: Invoice | null;
  loading: boolean;
  error: string | null;
  filters: {
    status?: string;
    client?: string;
    dateRange?: { start: Date; end: Date };
  };

  // Actions
  fetchInvoices: (params?: any) => Promise<void>;
  fetchPendingTasks: () => Promise<void>;
  generateInvoiceFromTasks: (data: {
    taskIds: string[];
    clientName: string;
    dueDate: string;
    notes?: string;
  }) => Promise<any>;
  updateClientAndGenerateInvoice: (data: {
    clientId: string;
    address: string;
    gst: string;
    taskIds: string[];
    dueDate: string;
  }) => Promise<void>;
  downloadInvoice: (id: string) => Promise<void>;
  sendInvoice: (id: string, email: string) => Promise<void>;
  revertInvoice: (id: string) => Promise<void>;
  markInvoiceAsDone: (id: string) => Promise<void>;
  setSelectedInvoice: (invoice: Invoice | null) => void;
  setFilters: (filters: any) => void;
}

export const useInvoiceStore = create<InvoiceState>()(
  devtools(
    (set, get) => ({
      invoices: [],
      pendingTasks: [],
      selectedInvoice: null,
      loading: false,
      error: null,
      filters: {},

      fetchInvoices: async (params) => {
        set({ loading: true, error: null });
        try {
          const response = await invoicesApi.getInvoices({ ...get().filters, ...params });
          set({ 
            invoices: response?.data?.data?.invoices || [], 
            loading: false 
          });
        } catch (error: any) {
          console.error('Error fetching invoices:', error);
          set({ error: error.message, loading: false });
          toast.error('Failed to fetch invoices');
        }
      },

      fetchPendingTasks: async () => {
        set({ loading: true, error: null });
        try {
          const response = await invoicesApi.getInvoices({ status: TASK_STATUS.PENDING });
          set({ 
            pendingTasks: response?.data?.data?.tasks || [], 
            loading: false 
          });
        } catch (error: any) {
          console.error('Error fetching pending tasks:', error);
          set({ error: error.message, loading: false });
          toast.error('Failed to fetch pending tasks');
        }
      },
updateClient: async (id: string, updates: Partial<Client>) => {
  try {
    const response = await clientsApi.updateClient(id, updates);
    const updatedClient = response?.data?.data;
    if (updatedClient) {
      set((state) => ({
        clients: state.clients.map(client => 
          client._id === id ? updatedClient : client
        ),
      }));
    }
    return updatedClient;
  } catch (error: any) {
    console.error('Error updating client:', error);
    throw error;
  }
},
      generateInvoiceFromTasks: async (data) => {
        try {
          const response = await invoicesApi.generateFromTasks(data);
          const responseData = response?.data?.data;
          
          if (responseData?.requiresBusinessDetails) {
            return responseData;
          }
          
          if (responseData?.invoice) {
            set((state) => ({
              invoices: [...state.invoices, responseData.invoice],
              selectedInvoice: responseData.invoice
            }));
            toast.success('Invoice generated successfully');
            return responseData;
          }
        } catch (error: any) {
          console.error('Error generating invoice:', error);
          const errorMessage = error.response?.data?.message || 'Failed to generate invoice';
          toast.error(errorMessage);
          
          if (error.response?.data?.data?.requiresBusinessDetails) {
            return error.response.data.data;
          }
          
          throw error;
        }
      },

      updateClientAndGenerateInvoice: async (data) => {
        try {
          const response = await invoicesApi.updateClientAndGenerate(data);
          const invoice = response?.data?.data?.invoice;
          
          if (invoice) {
            set((state) => ({
              invoices: [...state.invoices, invoice],
              selectedInvoice: invoice
            }));
            toast.success('Invoice generated successfully');
          }
        } catch (error: any) {
          console.error('Error updating client and generating invoice:', error);
          toast.error(error.response?.data?.message || 'Failed to generate invoice');
        }
      },

      downloadInvoice: async (id) => {
        try {
          const response = await invoicesApi.downloadInvoice(id);
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `invoice-${id}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success('Invoice downloaded successfully');
        } catch (error: any) {
          console.error('Error downloading invoice:', error);
          toast.error('Failed to download invoice');
        }
      },

      sendInvoice: async (id, email) => {
        try {
          await invoicesApi.sendInvoice(id, email);
          set((state) => ({
            invoices: state.invoices.map(invoice => 
              invoice._id === id ? { ...invoice, status: 'Sent' } : invoice
            )
          }));
          toast.success(`Invoice sent to ${email}`);
        } catch (error: any) {
          console.error('Error sending invoice:', error);
          toast.error('Failed to send invoice');
        }
      },

      revertInvoice: async (id) => {
        try {
          await invoicesApi.revertInvoice(id);
          set((state) => ({
            invoices: state.invoices.filter(invoice => invoice._id !== id)
          }));
          toast.success('Invoice reverted to pending');
          get().fetchPendingTasks();
        } catch (error: any) {
          console.error('Error reverting invoice:', error);
          toast.error('Failed to revert invoice');
        }
      },

      markInvoiceAsDone: async (id) => {
        try {
          await invoicesApi.markAsDone(id);
          set((state) => ({
            invoices: state.invoices.map(invoice => 
              invoice._id === id ? { ...invoice, status: 'Done' } : invoice
            )
          }));
          toast.success('Invoice marked as done');
        } catch (error: any) {
          console.error('Error marking invoice as done:', error);
          toast.error('Failed to update invoice status');
        }
      },

      setSelectedInvoice: (invoice) => {
        set({ selectedInvoice: invoice });
      },

      setFilters: (filters) => {
        set({ filters });
        get().fetchInvoices();
      }
    }),
    {
      name: 'invoice-store',
    }
  )
);